import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import UpgradeModal from "@/components/UpgradeModal";
import {
  ArrowLeft,
  Mic,
  Send,
  Sparkles,
  User as UserIcon,
  Volume2,
  Loader2,
  CheckCircle2,
  Trophy,
} from "lucide-react";

const OsceRolePlayPage = () => {
  const navigate = useNavigate();
  const { scenarioId } = useParams();

  const [session, setSession] = useState(null); // {session_id, scenario_title}
  const [messages, setMessages] = useState([]); // [{role, content}]
  const [input, setInput] = useState("");
  const [loadingStart, setLoadingStart] = useState(true);
  const [sending, setSending] = useState(false);
  const [ended, setEnded] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [grading, setGrading] = useState(false);
  const [voicePending, setVoicePending] = useState({}); // {messageIndex: bool}
  const [voiceUnavailable, setVoiceUnavailable] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      try {
        const res = await api.post("/tutor/osce/start", { scenario_id: scenarioId });
        setSession({ session_id: res.session_id, scenario_title: res.scenario_title });
        setMessages([{ role: "assistant", content: res.patient_message }]);
      } catch (e) {
        console.error(e);
        if (e?.response?.status === 402) {
          setShowUpgrade(true);
          return;
        }
        toast.error("Could not start the role-play.");
        navigate(`/cases/${scenarioId}`);
      } finally {
        setLoadingStart(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, feedback]);

  const sendTurn = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending || ended) return;
    const studentMsg = input.trim();
    setMessages((prev) => [...prev, { role: "user", content: studentMsg }]);
    setInput("");
    setSending(true);
    try {
      const res = await api.post("/tutor/osce/turn", {
        session_id: session.session_id,
        message: studentMsg,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: res.patient_message }]);
      if (res.ended) setEnded(true);
    } catch (e) {
      console.error(e);
      toast.error("Patient lost connection. Try again.");
    } finally {
      setSending(false);
    }
  };

  const finishSession = async () => {
    if (!session || grading) return;
    setGrading(true);
    try {
      const res = await api.post("/tutor/osce/finish", { session_id: session.session_id });
      setFeedback(res.feedback);
      setEnded(true);
      toast.success(`+${res.xp_earned} points earned!`);
    } catch (e) {
      console.error(e);
      toast.error("Could not get feedback.");
    } finally {
      setGrading(false);
    }
  };

  const playVoice = async (text, idx) => {
    if (voiceUnavailable) return;
    setVoicePending((p) => ({ ...p, [idx]: true }));
    try {
      const res = await api.post("/tutor/tts", { text });
      const audio = new Audio(res.audio_url);
      audio.play();
    } catch (e) {
      // 503 = no API key configured yet; 402 = not Pro
      if (e?.response?.status === 503) {
        setVoiceUnavailable(true);
        toast.info("Voice mode is not configured yet. The role-play still works in text mode.");
      } else if (e?.response?.status === 402) {
        setShowUpgrade(true);
      } else {
        toast.error("Voice playback failed.");
      }
    } finally {
      setVoicePending((p) => ({ ...p, [idx]: false }));
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => e.data.size > 0 && audioChunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
          const formData = new FormData();
          formData.append("audio", blob, "voice.webm");
          const res = await fetch(`${BACKEND_URL}/api/tutor/stt`, {
            method: "POST",
            credentials: "include",
            body: formData,
          });
          if (res.status === 402) {
            setShowUpgrade(true);
            return;
          }
          if (!res.ok) throw new Error(`STT failed (${res.status})`);
          const data = await res.json();
          const txt = (data.text || "").trim();
          if (txt) setInput((cur) => (cur ? `${cur} ${txt}` : txt));
          else toast.info("I couldn't hear anything. Try again?");
        } catch (err) {
          console.error(err);
          toast.error("Couldn't transcribe your voice. Try again or type instead.");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      console.error(err);
      toast.error("Can't access your microphone. Check browser permissions.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  if (showUpgrade) {
    return (
      <>
        <div className="max-w-3xl mx-auto pb-32">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/cases/${scenarioId}`)} className="mb-3">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to case notes
          </Button>
        </div>
        <UpgradeModal
          open={true}
          onClose={() => navigate(`/cases/${scenarioId}`)}
          featureName="Voice patient role-play"
        />
      </>
    );
  }

  if (loadingStart) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Setting up the patient...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="osce-roleplay-page" className="max-w-3xl mx-auto pb-32">
      <Button variant="ghost" size="sm" onClick={() => navigate(`/cases/${scenarioId}`)} className="mb-3">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to case notes
      </Button>

      {/* Header */}
      <div className="mb-5">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-warning/10 border border-warning/20 mb-3">
          <Mic className="w-3.5 h-3.5 text-warning" />
          <span className="text-xs font-medium text-warning">OSCE Role-Play (BETA)</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
          {session?.scenario_title}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          You are the dentist. Take a history from your virtual patient, give your impression, then click "End consultation" for feedback.
        </p>
      </div>

      {/* Conversation */}
      <div className="glass rounded-2xl border border-border p-4 md:p-6 mb-5 min-h-[300px] space-y-4">
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
            data-testid={`osce-msg-${idx}`}
          >
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                m.role === "user" ? "bg-primary/20" : "bg-warning/20"
              }`}
            >
              {m.role === "user" ? (
                <UserIcon className="w-4 h-4 text-primary" />
              ) : (
                <span className="text-warning text-sm">🧑</span>
              )}
            </div>
            <div className={`flex-1 max-w-[80%] ${m.role === "user" ? "text-right" : ""}`}>
              <div className="flex items-center gap-2 mb-1 text-xs text-muted-foreground">
                {m.role === "user" ? <span>You (Doctor)</span> : <span>Patient</span>}
                {m.role === "assistant" && !voiceUnavailable && (
                  <button
                    data-testid={`osce-voice-${idx}`}
                    onClick={() => playVoice(m.content, idx)}
                    className="text-muted-foreground hover:text-warning transition-colors"
                    title="Play patient voice"
                  >
                    {voicePending[idx] ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Volume2 className="w-3.5 h-3.5" />
                    )}
                  </button>
                )}
              </div>
              <div
                className={`inline-block px-4 py-3 rounded-2xl text-sm ${
                  m.role === "user"
                    ? "bg-primary/15 border border-primary/20 rounded-br-sm text-left"
                    : "bg-secondary border border-border rounded-bl-sm"
                }`}
              >
                {m.content}
              </div>
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex gap-3">
            <div className="w-9 h-9 rounded-full bg-warning/20 flex items-center justify-center flex-shrink-0">
              <span className="text-warning text-sm">🧑</span>
            </div>
            <div className="px-4 py-3 rounded-2xl bg-secondary border border-border inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-warning animate-pulse" />
              <span className="w-2 h-2 rounded-full bg-warning animate-pulse" style={{ animationDelay: "0.2s" }} />
              <span className="w-2 h-2 rounded-full bg-warning animate-pulse" style={{ animationDelay: "0.4s" }} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Feedback panel */}
      {feedback && (
        <div data-testid="osce-feedback" className="glass rounded-2xl border border-success/30 bg-success/5 p-6 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-5 h-5 text-success" />
            <h3 className="font-semibold">Examiner feedback</h3>
            <span className="ml-auto text-xs text-muted-foreground">+75 points</span>
          </div>
          <div className="prose prose-invert prose-sm max-w-none">
            <ReactMarkdown
              components={{
                strong: ({ children }) => (
                  <strong className="text-success font-semibold">{children}</strong>
                ),
                ul: ({ children }) => <ul className="list-disc pl-5 space-y-1.5 my-2">{children}</ul>,
              }}
            >
              {feedback}
            </ReactMarkdown>
          </div>
          <Button
            data-testid="osce-finish-back-btn"
            onClick={() => navigate("/cases")}
            className="rounded-full mt-4"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Back to all cases
          </Button>
        </div>
      )}

      {/* Composer */}
      {!ended && !feedback && (
        <form onSubmit={sendTurn} className="sticky bottom-4 glass rounded-2xl border border-border p-3 flex items-end gap-2">
          <textarea
            data-testid="osce-input"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendTurn(e);
              }
            }}
            placeholder="Ask the patient something... (e.g. 'When did the pain start?') - or tap the mic to speak"
            className="flex-1 bg-transparent text-sm focus:outline-none px-2 py-1 resize-none"
            disabled={sending}
          />
          <Button
            data-testid="osce-mic-btn"
            type="button"
            onClick={recording ? stopRecording : startRecording}
            disabled={sending || transcribing}
            title={recording ? "Stop recording" : "Speak your question"}
            className={`rounded-full h-10 w-10 p-0 flex-shrink-0 ${
              recording
                ? "bg-destructive hover:bg-destructive/80 animate-pulse"
                : "bg-warning/20 hover:bg-warning/30 text-warning border border-warning/30"
            }`}
          >
            {transcribing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Mic className="w-4 h-4" />
            )}
          </Button>
          <Button
            data-testid="osce-send-btn"
            type="submit"
            disabled={!input.trim() || sending}
            className="rounded-full h-10 w-10 p-0 flex-shrink-0"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      )}

      {recording && (
        <div className="text-center -mt-2 mb-2">
          <p className="text-xs text-destructive animate-pulse">🔴 Recording... tap the mic again to stop</p>
        </div>
      )}

      {/* End consultation */}
      {!feedback && (
        <div className="mt-3 text-center">
          <Button
            data-testid="osce-end-btn"
            variant="outline"
            onClick={finishSession}
            disabled={grading || messages.length < 3}
            className="rounded-full"
          >
            {grading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Examiner grading...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> End consultation & get feedback</>
            )}
          </Button>
          {messages.length < 3 && (
            <p className="text-xs text-muted-foreground mt-2">
              Ask the patient at least one question first.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OsceRolePlayPage;
